/**
 * RecentActivitiesTable Component
 * Displays recent calls, website activity, and emails in a table format
 */

import { FC, useState } from 'react';
import { useApolloClient } from '@apollo/client/react';
import { Badge, Table } from '@crisellevaldez/speculo-ui';
import { PhoneIncoming, PhoneOutgoing, ExternalLink } from 'lucide-react';
import { formatDate } from '../../../../utils/dateTime';
import { formatCallDuration } from '../../../../utils/duration';
import { getCallOutcomeStyle, getWebActivityEventTypeStyle } from '../../../../utils/badgeStyles';
import { getEmailEventStyle } from './handoffStyles';
import { GET_ORG_CALL_BY_ID } from '../../../../lib/graphql/queries/organizations';
import { CallDetailsModal, type CallRecord } from '../../calls/CallDetailsModal';
import { useOrganizationStore } from '../../../../stores/useOrganizationStore';

/**
 * Map a GQL call payload (GET_ORG_CALL_BY_ID) to the CallRecord shape
 * CallDetailsModal expects. Mirrors adaptOrgCallForPanel in ActivityTab.
 */
function adaptCallForModal(c: Record<string, unknown>): CallRecord {
  const tryParse = (v: unknown) => {
    if (typeof v !== 'string') return undefined;
    try {
      return JSON.parse(v);
    } catch {
      return undefined;
    }
  };
  const timeline = (() => {
    const parsed = tryParse(c.callAnalysisTimeline);
    return Array.isArray(parsed) ? parsed : undefined;
  })();
  const answers = tryParse(c.callAnalysisAnswers);
  return {
    id: c.callId as string,
    call_id: c.callId as string,
    inbound: (c.inbound as boolean) ?? false,
    callTimestamp: c.initiatedAt ? new Date(c.initiatedAt as string) : undefined,
    duration: (c.durationSeconds as number) ?? 0,
    corrected_duration: (c.durationSeconds as number) ?? 0,
    answered_by: (c.answeredBy as CallRecord['answered_by']) ?? undefined,
    call_analysis: {
      outcome: (c.outcome as string) ?? '',
      summary: (c.summary as string) ?? undefined,
      timeline,
      answers,
    },
    concatenated_transcript: (c.transcript as string) ?? undefined,
    recordingUrl: (c.recordingUrl as string) ?? undefined,
    flowName: (c.flowName as string) ?? undefined,
    flowType: (c.flowType as string) ?? undefined,
  };
}

export interface CallActivity {
  callId: string;
  outcome: string | null;
  /** Call status from the backend (e.g. 'completed'); used as the badge fallback when outcome is null. */
  status?: string | null;
  summary?: string;
  /** Canonical name from the backend. Older callers may still pass `duration`. */
  durationSeconds?: number;
  duration?: number;
  /** Canonical name from the backend. Some legacy callers pass `createdAt`. */
  initiatedAt?: string;
  createdAt?: string;
  recordingUrl?: string;
  /** Canonical name from the backend. Some legacy callers pass `inbound`. */
  direction?: 'inbound' | 'outbound';
  inbound?: boolean;
}

export interface WebsiteActivity {
  activityId?: string;
  id?: string;
  deviceType?: 'mobile' | 'desktop' | 'tablet';
  eventType: string;
  pageUrl: string;
  pageTitle?: string;
  timestamp: string;
  properties?: {
    address?: string;
    city?: string;
    state?: string;
    searchQuery?: string;
  };
}

export interface EmailActivity {
  logId?: string;
  id?: string;
  eventType?: string;
  subjectLine?: string | null;
  subject?: string;
  contactEmail?: string;
  bodyHtml?: string;
  templateName?: string | null;
  timestamp?: string;
  sentAt?: string;
  opened?: boolean;
  clicked?: boolean;
}

interface RecentActivitiesTableProps {
  calls: CallActivity[];
  websiteActivity: WebsiteActivity[];
  emails: EmailActivity[];
  orgTimezone?: string;
  /** The handoff's lead, for the call details header. */
  contactId?: string | null;
  contactName?: string | null;
  contactPhone?: string | null;
}

export const RecentActivitiesTable: FC<RecentActivitiesTableProps> = ({
  calls,
  websiteActivity,
  emails,
  orgTimezone,
  contactId,
  contactName,
  contactPhone,
}) => {
  const apolloClient = useApolloClient();
  const { organization } = useOrganizationStore();
  const [selectedCall, setSelectedCall] = useState<CallRecord | null>(null);
  const [isLoadingCall, setIsLoadingCall] = useState(false);

  // Fetch the full call payload by id, then open the details modal.
  const openCallDetails = async (callId: string) => {
    if (!organization?.id || !callId) return;
    setIsLoadingCall(true);
    try {
      const { data } = await apolloClient.query<{
        organization?: { calls?: { items?: Record<string, unknown>[] } } | null;
      }>({
        query: GET_ORG_CALL_BY_ID,
        variables: { organizationId: organization.id, callId },
        fetchPolicy: 'cache-first',
      });
      const fresh = data?.organization?.calls?.items?.[0];
      if (fresh) setSelectedCall(adaptCallForModal(fresh));
    } catch (err) {
      console.warn('[RecentActivitiesTable] failed to fetch call', err);
    } finally {
      setIsLoadingCall(false);
    }
  };

  // Hide noisy page_view / custom events — match dashboard + contact activity feed.
  const filteredWebsiteActivity = websiteActivity.filter(
    (activity) => activity.eventType !== 'page_view' && activity.eventType !== 'custom'
  );

  // Combine data into rows - each row has a call, website activity, and email
  const maxRows = Math.max(calls.length, filteredWebsiteActivity.length, emails.length);
  const combinedData = Array.from({ length: maxRows }, (_, index) => ({
    id: `row-${index}`,
    call: calls[index] || null,
    website: filteredWebsiteActivity[index] || null,
    email: emails[index] || null,
  }));

  // Single table with 3 columns: Calls, Website, Emails
  const columns = [
    {
      header: 'Calls',
      key: 'calls',
      width: '400px',
      cell: (item: { call: CallActivity | null }) => {
        if (!item.call) return <span className="text-muted-foreground text-xs">-</span>;
        const outcomeStyle = item.call.outcome ? getCallOutcomeStyle(item.call.outcome) : null;
        // Backend returns durationSeconds; older callers may pass duration.
        const rawDuration = item.call.durationSeconds ?? item.call.duration;
        const durationSecs =
          typeof rawDuration === 'string' ? parseInt(rawDuration, 10) : (rawDuration ?? 0);
        const durationStr = durationSecs > 0 ? formatCallDuration(durationSecs, 'seconds') : '';
        const isInbound = item.call.inbound ?? item.call.direction === 'inbound';
        const timestamp = item.call.initiatedAt ?? item.call.createdAt;

        const callId = item.call.callId;

        return (
          <button
            type="button"
            onClick={() => openCallDetails(callId)}
            disabled={isLoadingCall}
            className="flex items-center gap-2 w-full text-left hover:bg-accent rounded p-1 -m-1 disabled:opacity-60"
            title="View call details"
          >
            {isInbound ? (
              <PhoneIncoming className="h-4 w-4 fill-[#1C5499] text-[#1C5499] shrink-0" />
            ) : (
              <PhoneOutgoing className="h-4 w-4 fill-[#59987A] text-[#59987A] shrink-0" />
            )}
            {item.call.outcome && outcomeStyle && (
              <Badge className={`${outcomeStyle.background} ${outcomeStyle.text}`}>
                {item.call.outcome.replace(/_/g, ' ')}
              </Badge>
            )}
            {durationStr && <span className="text-foreground text-xs">{durationStr}</span>}
            {timestamp && (
              <span className="text-foreground text-xs hidden sm:inline ml-2">
                {formatDate(timestamp, orgTimezone)}
              </span>
            )}
          </button>
        );
      },
    },
    {
      header: 'Website',
      key: 'website',
      cell: (item: { website: WebsiteActivity | null }) => {
        if (!item.website) return <span className="text-muted-foreground text-xs">-</span>;
        const rawEventType = item.website.eventType || 'page_view';
        const eventStyle = getWebActivityEventTypeStyle(rawEventType);
        const eventLabel = rawEventType
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase());
        return (
          <div className="flex items-center gap-2">
            <Badge className={`${eventStyle.background} ${eventStyle.text} pointer-events-none`}>
              {eventLabel}
            </Badge>
            {item.website.pageUrl && (
              <a
                href={item.website.pageUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:text-primary/80"
                title={item.website.pageUrl}
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </a>
            )}
          </div>
        );
      },
    },
    {
      header: 'Emails',
      key: 'emails',
      cell: (item: { email: EmailActivity | null }) => {
        if (!item.email) return <span className="text-muted-foreground text-xs">-</span>;
        const eventType = item.email.eventType || 'sent';
        const eventLabel = eventType
          .replace(/_/g, ' ')
          .replace(/\b\w/g, (c: string) => c.toUpperCase());

        const emailStyle = getEmailEventStyle(eventType);

        // Handle opening bodyHtml in a new window
        const handleViewEmail = () => {
          if (item.email?.bodyHtml) {
            const newWindow = window.open('', '_blank');
            if (newWindow) {
              newWindow.document.write(item.email.bodyHtml);
              newWindow.document.close();
            }
          }
        };

        return (
          <div className="flex items-center gap-2">
            <Badge className={`${emailStyle.bg} ${emailStyle.text} pointer-events-none`}>
              {eventLabel}
            </Badge>
            {item.email.bodyHtml && (
              <button
                onClick={handleViewEmail}
                className="text-primary hover:text-primary/80"
                title="View email content"
              >
                <ExternalLink className="w-3.5 h-3.5" />
              </button>
            )}
          </div>
        );
      },
    },
  ];

  // Check if there's any data at all
  const hasData = calls.length > 0 || filteredWebsiteActivity.length > 0 || emails.length > 0;

  if (!hasData) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 text-center text-muted-foreground text-sm">
        No recent activities
      </div>
    );
  }

  return (
    <>
      <div className="bg-card rounded-lg border border-border overflow-hidden">
        <Table
          data={combinedData}
          columns={columns}
          keyExtractor={(item) => item.id}
          emptyStateText="No recent activities"
          size="sm"
        />
      </div>
      <CallDetailsModal
        isOpen={!!selectedCall}
        onClose={() => setSelectedCall(null)}
        call={selectedCall}
        contactId={contactId}
        contactName={contactName}
        contactPhone={contactPhone}
      />
    </>
  );
};

export default RecentActivitiesTable;
